import { LitElement, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { SELECTORS } from '../../constants/selectors';
import { KEYWORD_HISTORY_LIMIT, pushKeywordHistory } from './keyword-history.logic';

/** パネルを開いている間 body に付けるクラス（背面のスクロールを止める） */
const SCROLL_LOCK_CLASS = 'tw:overflow-hidden';

/**
 * @element kd-search-panel
 * @description ヘッダーの検索窓のフォーカスで、画面全体に重ねて開く検索パネル（課題管理表_phase2,3 #160）。
 *
 * マークアップは snippets/v2-search-panel.liquid（この要素自身がパネル。初期は hidden）。
 * 本コンポーネントは開閉だけを受け持つ。
 *
 * - `[data-search-panel-trigger]`（ヘッダーの PC / SP 検索窓）に focus が入ったら（focus 済みならクリックで）開き、
 *   パネル内の入力欄へ**同期的に** focus を移す。iOS Safari はユーザー操作の流れの中で
 *   focus しないとキーボードを出さないため、requestAnimationFrame などで遅らせない
 * - 開くたびに検索窓の文字をパネルの入力欄へ写す（空なら空にする＝前回の語を残さない）
 * - パネルから検索したキーワードも履歴に保存する。ヘッダーの検索窓は履歴サジェストを出さなくなったが、
 *   SP メニュー内の検索フォームは今も履歴を出すので、保存を止めるとそちらの履歴が古いまま止まる
 * - 開いている間は Tab / Shift+Tab をパネルの中で循環させる（aria-modal のダイアログなので背面へ抜けない）
 * - × のクリック / Escape で閉じる。キーボードで閉じたとき（Escape・Enter/Space で ×）は
 *   開いた検索窓へ focus を戻す（戻した focus でパネルが開き直さないよう 1 回だけ抑止する）。
 *   タップで閉じたときは戻さない（戻すと SP で検索窓のキーボードが出てしまう）
 */
@customElement('kd-search-panel')
export class KdSearchPanel extends LitElement {
  createRenderRoot() {
    return this;
  }

  private input: HTMLInputElement | null = null;

  private form: HTMLFormElement | null = null;

  /** パネルを開いた検索窓（閉じたときの focus の戻り先） */
  private opener: HTMLInputElement | null = null;

  /** 閉じたときに戻した focus で、パネルが開き直すのを 1 回だけ止める */
  private suppressNextOpen = false;

  render() {
    return nothing;
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.input = this.querySelector<HTMLInputElement>(SELECTORS.HEADER.SEARCH_PANEL_INPUT);
    this.form = this.querySelector<HTMLFormElement>(SELECTORS.HEADER.SEARCH_PANEL_FORM);
    document.addEventListener('focusin', this.handleFocusIn);
    document.addEventListener('click', this.handleTriggerClick);
    document.addEventListener('keydown', this.handleKeydown);
    this.addEventListener('click', this.handleClick);
    this.form?.addEventListener('submit', this.handleSubmit);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('focusin', this.handleFocusIn);
    document.removeEventListener('click', this.handleTriggerClick);
    document.removeEventListener('keydown', this.handleKeydown);
    this.removeEventListener('click', this.handleClick);
    this.form?.removeEventListener('submit', this.handleSubmit);
    document.body.classList.remove(SCROLL_LOCK_CLASS);
  }

  get isOpen(): boolean {
    return !this.hidden;
  }

  open(trigger?: HTMLInputElement): void {
    if (this.isOpen) return;
    this.opener = trigger ?? null;
    this.hidden = false;
    document.body.classList.add(SCROLL_LOCK_CLASS);
    if (this.input) {
      if (trigger) this.input.value = trigger.value;
      this.input.focus();
    }
  }

  close({ restoreFocus = false }: { restoreFocus?: boolean } = {}): void {
    if (!this.isOpen) return;
    this.hidden = true;
    document.body.classList.remove(SCROLL_LOCK_CLASS);
    if (restoreFocus && this.opener && this.opener.isConnected) {
      this.suppressNextOpen = true;
      this.opener.focus();
      // focusin が来なかった場合（すでに focus 済みなど）に抑止を持ち越さない
      this.suppressNextOpen = false;
    } else {
      this.input?.blur();
    }
  }

  private handleFocusIn = (e: FocusEvent): void => {
    if (this.suppressNextOpen) {
      this.suppressNextOpen = false;
      return;
    }
    this.openFrom(e.target);
  };

  /** すでに focus が入っている検索窓（Escape で戻した直後など）は、クリックしても focusin が来ない */
  private handleTriggerClick = (e: MouseEvent): void => {
    this.openFrom(e.target);
  };

  private openFrom(target: EventTarget | null): void {
    if (!(target instanceof HTMLInputElement)) return;
    if (!target.matches(SELECTORS.HEADER.SEARCH_PANEL_TRIGGER)) return;
    this.open(target);
  }

  private handleKeydown = (e: KeyboardEvent): void => {
    if (!this.isOpen) return;
    // 日本語入力の変換中の Escape は「変換の取り消し」なので閉じない（PR #3931 greptile P2）。
    // Safari は変換中のキー入力を isComposing ではなく keyCode 229 で知らせるため両方見る
    if (e.key === 'Escape' && !e.isComposing && e.keyCode !== 229) {
      this.close({ restoreFocus: true });
      return;
    }
    if (e.key === 'Tab') this.trapTab(e);
  };

  /** Tab / Shift+Tab がパネルの端に来たら反対側の端へ回す */
  private trapTab(e: KeyboardEvent): void {
    const focusables = Array.from(
      this.querySelectorAll<HTMLElement>(SELECTORS.HEADER.SEARCH_PANEL_FOCUSABLE),
    );
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;
    const outside = !(active instanceof Node) || !this.contains(active);
    if (e.shiftKey && (active === first || outside)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && (active === last || outside)) {
      e.preventDefault();
      first.focus();
    }
  }

  private handleClick = (e: MouseEvent): void => {
    const target = e.target as Element | null;
    if (target?.closest(SELECTORS.HEADER.SEARCH_PANEL_CLOSE)) {
      e.preventDefault();
      // detail === 0 はキーボード（Enter / Space）で押されたクリック
      this.close({ restoreFocus: e.detail === 0 });
    }
  };

  private handleSubmit = (): void => {
    if (this.input) pushKeywordHistory(this.input.value, KEYWORD_HISTORY_LIMIT);
    // submit はそのまま進む（preventDefault しない）
  };
}
